import React, { useState, useRef, useEffect } from "react";
import { Phone, MapPin, Mail, ArrowRight } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import "./Contact.css";

const WHATSAPP_NUMBER = "917574098964"; // no + or spaces, country code included
const PHONE_NUMBER = "+917574098964";
const EMAIL_ADDRESS = "bholecabservice25@gmail.com";
const MAPS_DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=Ambika+Dham+Complex,+Suryanagar,+Vadodara,+Gujarat+390025";

const serviceOptions = [
  "Select Service",
  "One Way Trip",
  "Round Trip",
  "Vadodara Local Taxi Service",
  "Airport Pick & Drop",
  "Wedding & Event Cab Service",
  "Corporate Travel",
];

/* ---------- Custom themed dropdown ---------- */
const CustomSelect = ({ id, options, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setOpen(false);
  };

  return (
    <div className="custom-select" ref={wrapperRef}>
      <button
        type="button"
        id={id}
        className={`select-trigger ${!value ? "placeholder" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{value || placeholder}</span>
        <svg
          className={`select-chevron ${open ? "open" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="m6 9 6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <ul className="select-menu" role="listbox">
          {options.map((option) => (
            <li
              key={option}
              role="option"
              aria-selected={value === option}
              className={`select-option ${value === option ? "selected" : ""}`}
              onClick={() => handleSelect(option)}
            >
              {option}
            </li>
          ))}
        </ul>
      )}

      <input
        tabIndex={-1}
        aria-hidden="true"
        className="select-native-shadow"
        value={value}
        required
        onChange={() => {}}
      />
    </div>
  );
};

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    service: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendWhatsApp = (e) => {
    e.preventDefault();

    const text = `Hello Bhole Cab Service, I would like to book a ride.
Name: ${form.name}
Mobile Number: ${form.mobile}
Service Type: ${form.service}
Message: ${form.message}`;

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      text
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="contact" id="contact">
      <div className="contact-container">
        {/* Decorative Elements */}
        <div className="contact-decoration"></div>

        {/* Header */}
        <div className="contact-header">
          <div className="contact-eyebrow">
            <span className="contact-eyebrow-line"></span>
            Get In Touch
          </div>
          <h2>Book Your <span>Ride</span> Today</h2>
          <p>Fill out the form below and our team will get back to you promptly. Fast, reliable, and safe cab service across India.</p>
        </div>

        <div className="contact-grid">
          {/* ---------- Left: Booking Form ---------- */}
          <div className="contact-card form-card">
            <h3>Book Your Ride</h3>
            
            <form onSubmit={handleSendWhatsApp}>
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter your name"
                value={form.name}
                onChange={handleChange}
                required
              />

              <label htmlFor="mobile">Mobile Number</label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                placeholder="Enter mobile number"
                value={form.mobile}
                onChange={handleChange}
                required
              />

              <label htmlFor="service">Service Type</label>
              <CustomSelect
                id="service"
                options={serviceOptions.slice(1)}
                value={form.service}
                placeholder={serviceOptions[0]}
                onChange={(val) =>
                  setForm((prev) => ({ ...prev, service: val }))
                }
              />

              <label htmlFor="message">Message / Details</label>
              <textarea
                id="message"
                name="message"
                rows="4"
                placeholder="Enter any additional details"
                value={form.message}
                onChange={handleChange}
              />

              <button type="submit" className="whatsapp-btn">
                <FaWhatsapp className="whatsapp-icon" />
                <span>Send via WhatsApp</span>
                <ArrowRight size={16} className="arrow-icon" />
              </button>
            </form>
          </div>

          {/* ---------- Right: Map + Contact Details ---------- */}
          <div className="contact-side">
            <div className="contact-card map-card">
              <a
                className="map-overlay-link"
                href={MAPS_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MapPin size={16} />
                <span>Open in Maps</span>
              </a>
              <iframe
                title="Bhole Cab Service Location"
                src="https://www.google.com/maps?q=Ambika+Dham+Complex,+Suryanagar,+Vadodara,+Gujarat+390025&output=embed"
                width="100%"
                height="280"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            <div className="contact-card details-card">
              <h3>Contact Details</h3>

              <div className="detail-row">
                <div className="detail-icon-wrapper">
                  <MapPin size={18} />
                </div>
                <div className="detail-content">
                  <h4>Bhole Cab Service</h4>
                  <a
                    href={MAPS_DIRECTIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="detail-link"
                  >
                    Vadodara, Gujarat 390025
                  </a>
                </div>
              </div>

              <div className="detail-row">
                <div className="detail-icon-wrapper">
                  <Phone size={18} />
                </div>
                <a href={`tel:${PHONE_NUMBER}`} className="detail-link">
                  {PHONE_NUMBER}
                </a>
              </div>

              <div className="detail-row">
                <div className="detail-icon-wrapper">
                  <Mail size={18} />
                </div>
                <a href={`mailto:${EMAIL_ADDRESS}`} className="detail-link">
                  {EMAIL_ADDRESS}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;